import React from 'react';
import { differenceInDays, startOfDay } from 'date-fns';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { SubscriptionStatus } from '@prisma/client';
import useGetSubscription from './useGetSubscription';
import useGetSubscriptionFromStripe from './useGetSubscriptionFromStripe';
import { DEV } from '../../constants/global';
import { isReactNativeWebView } from '../../utils/global/helper';

const KEY = 'free-trial-banner-visible';

function useFreeTrialStatus() {
  const queryClient = useQueryClient();
  const subData = useGetSubscription();
  // only use when already available - do not trigger fetch
  const stripeSubData = useGetSubscriptionFromStripe({
    enabled: DEV,
  });
  const { testClock } = stripeSubData?.data || {};

  const { data: isVisible = true } = useQuery({
    queryKey: ['free-trial-banner-visible'],
    queryFn: () => {
      const value = sessionStorage.getItem(KEY);
      return !value || value === 'true';
    },
  });

  const now = React.useMemo(
    () => (testClock ? new Date(testClock.frozen_time * 1000) : new Date()),
    [testClock],
  );

  const inFreeTrial = React.useMemo(
    () =>
      subData?.data?.subscription?.status === SubscriptionStatus.TRIALING ||
      (subData?.data?.subscription?.trialEndsAt &&
        now < new Date(subData.data.subscription.trialEndsAt)),
    [
      now,
      subData.data?.subscription?.status,
      subData.data?.subscription?.trialEndsAt,
    ],
  );

  const daysRemaining = React.useMemo(() => {
    if (!inFreeTrial || !subData.data?.subscription) return 0;
    return differenceInDays(
      startOfDay(new Date(subData.data.subscription.trialEndsAt!)),
      startOfDay(now),
    );
  }, [inFreeTrial, now, subData.data?.subscription]);

  const hideBanner = () => {
    queryClient.setQueryData(['free-trial-banner-visible'], false);
    sessionStorage.setItem(KEY, 'false');
  };

  return {
    inFreeTrial,
    daysRemaining,
    isBannerShown: inFreeTrial && isVisible && !isReactNativeWebView(),
    hideBanner,
  };
}

export default useFreeTrialStatus;
